/* ==========================================================================
   AECOOLY — global behaviour
   --------------------------------------------------------------------------
   Deliberately small and dependency-free. No animation library: everything
   here either sets a class and lets CSS transitions do the work (which runs
   off the main thread) or uses the Web Animations API. That keeps motion
   smooth even while the browser is busy fetching product images.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Scroll reveal
     The revealed state is the CSS default, so content is visible if this
     script never runs, fails, or the user prefers reduced motion. The hidden
     state is only ever applied by CSS gated behind `.js` + no-preference.
     ------------------------------------------------------------------------ */
  function initReveal() {
    var targets = document.querySelectorAll('.ae-reveal');
    if (!targets.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('ae-reveal--in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var el = entry.target;
          // Stagger is per-group, so each grid cascades on its own rather
          // than inheriting a delay from how far down the page it sits.
          var group = el.closest('[data-ae-stagger]');
          if (group) {
            var siblings = Array.prototype.slice.call(
              group.querySelectorAll('.ae-reveal')
            );
            var index = siblings.indexOf(el);
            // Cap the cascade: past ~8 items the last card would visibly
            // lag behind the user's scroll instead of feeling like one move.
            var step = parseInt(group.dataset.aeStagger, 10) || 50;
            el.style.setProperty(
              '--ae-reveal-delay',
              Math.min(index, 8) * step + 'ms'
            );
          }

          el.classList.add('ae-reveal--in');
          observer.unobserve(el); // reveal once; re-animating on scroll-back is noise
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Header scroll state
     Drives the scroll-edge effect on the translucent header — the material
     only gains its shadow once content is actually passing underneath it.
     ------------------------------------------------------------------------ */
  function initHeaderScroll() {
    var header = document.querySelector('[data-ae-header]');
    if (!header) return;

    var ticking = false;

    function update() {
      header.classList.toggle('ae-header--scrolled', window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------------
     Rail affordances
     Hides the prev/next buttons when a horizontal rail is at its extremes,
     so the control never lies about what it will do.
     ------------------------------------------------------------------------ */
  function initRails() {
    document.querySelectorAll('[data-ae-rail]').forEach(function (wrapper) {
      var rail = wrapper.querySelector('.ae-rail');
      var prev = wrapper.querySelector('[data-ae-rail-prev]');
      var next = wrapper.querySelector('[data-ae-rail-next]');
      if (!rail) return;

      function sync() {
        // 2px tolerance: sub-pixel layout means scrollLeft rarely lands
        // exactly on the maximum.
        var atStart = rail.scrollLeft <= 2;
        var atEnd =
          rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;

        if (prev) {
          prev.disabled = atStart;
          prev.setAttribute('aria-disabled', String(atStart));
        }
        if (next) {
          next.disabled = atEnd;
          next.setAttribute('aria-disabled', String(atEnd));
        }
        wrapper.classList.toggle('ae-rail-wrap--start', atStart);
        wrapper.classList.toggle('ae-rail-wrap--end', atEnd);
      }

      function page(direction) {
        var first = rail.querySelector(':scope > *');
        var step = first
          ? first.getBoundingClientRect().width + 16
          : rail.clientWidth * 0.8;
        rail.scrollBy({
          left: step * direction,
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });

      rail.addEventListener('scroll', function () {
        window.requestAnimationFrame(sync);
      }, { passive: true });

      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  function init() {
    initReveal();
    initHeaderScroll();
    initRails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify's theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', init);
})();
